import React from 'react';
import { motion } from 'framer-motion';
import { GameState, ArtistAction } from '@/types/career-sim';
import ActionCard from './ActionCard';

interface ActionCardDeckProps {
  gameState: GameState;
  onActionStart: (action: ArtistAction) => void;
}

const ActionCardDeck: React.FC<ActionCardDeckProps> = ({ gameState, onActionStart }) => {
  // Define available actions
  const actions: ArtistAction[] = [
    {
      id: 'create-music',
      name: 'Create Music',
      description: 'Create a new track or song',
      icon: '🎵',
      energyCost: 15,
      fundsCost: 0,
      location: 'bedroom-studio',
      outcomes: {
        creativity: 20,
        mentalHealth: -5
      },
      animation: 'creating',
      duration: 3,
      cooldown: 0
    },
    {
      id: 'record-track',
      name: 'Record Track',
      description: 'Record and produce a professional track',
      icon: '🎤',
      energyCost: 20,
      fundsCost: 200,
      location: 'bedroom-studio',
      outcomes: {
        creativity: 15,
        reputation: 10,
        funds: -200
      },
      animation: 'recording',
      duration: 5,
      cooldown: 0
    },
    {
      id: 'perform-live',
      name: 'Perform Live',
      description: 'Play a live show and gain fans',
      icon: '🎸',
      energyCost: 25,
      fundsCost: 0,
      location: 'local-venue',
      outcomes: {
        fanbase: 15,
        funds: 100,
        energy: -10
      },
      animation: 'performing',
      duration: 4,
      cooldown: 0
    },
    {
      id: 'network',
      name: 'Network',
      description: 'Connect with other music professionals',
      icon: '🤝',
      energyCost: 15,
      fundsCost: 0,
      location: 'social-media',
      outcomes: {
        social: 20,
        reputation: 5
      },
      animation: 'networking',
      duration: 3,
      cooldown: 0
    },
    {
      id: 'rest',
      name: 'Rest & Recover',
      description: 'Recharge your energy and mental health',
      icon: '💤',
      energyCost: -30,
      fundsCost: 0,
      location: 'bedroom-studio',
      outcomes: {
        energy: 30,
        mentalHealth: 20,
        physicalHealth: 15
      },
      animation: 'resting',
      duration: 2,
      cooldown: 0
    },
    {
      id: 'social-media',
      name: 'Social Media',
      description: 'Build your online presence',
      icon: '📱',
      energyCost: 10,
      fundsCost: 0,
      location: 'social-media',
      outcomes: {
        fanbase: 10,
        creativity: -5
      },
      animation: 'working',
      duration: 2,
      cooldown: 0
    }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h3 className="text-xl font-bold text-white mb-1">Choose Your Move</h3>
        <p className="text-sm text-white/60">Swipe or tap to take action</p>
      </motion.div>

      {/* Scrollable card container */}
      <div className="relative">
        {/* Scroll indicators */}
        <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[#0a0a1a] to-transparent pointer-events-none z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-[#0a0a1a] to-transparent pointer-events-none z-10" />

        {/* Cards */}
        <motion.div
          className="flex gap-4 overflow-x-auto pb-4 px-2 scrollbar-thin scrollbar-thumb-neon-purple/30 scrollbar-track-black/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {actions.map((action, index) => (
            <motion.div
              key={action.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <ActionCard
                action={action}
                gameState={gameState}
                onStart={onActionStart}
                disabled={gameState.activeAction !== null && gameState.activeAction.id !== action.id}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Hint text */}
      {!gameState.activeAction && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-white/40"
        >
          💡 Manage your energy carefully to keep progressing
        </motion.div>
      )}
    </div>
  );
};

export default ActionCardDeck;
